import { useEffect, useRef, useState } from 'react'
import { useApi } from '@/contexts/ApiContext'
import { isLabSkuInsuranceOutOfNetwork } from '@/lib/lab_order_utils'
import { STANDARD_CLINICAL_FEE } from '@/types/enums/lab-pricing-constants'
import type { LabSku, LabSkuResponse } from '@/types/interfaces/lab'

export interface LabSkuPricing {
    labSku: LabSku | null
    /** Lowest self-pay price across the reference labs, in dollars. */
    cashPrice: number | null
    /** Lowest quoted insurance price across the reference labs, in dollars. */
    insurancePrice: number | null
    /** The patient's insurance is out of network at the reference labs, so no insurance price is quoted. */
    isInsuranceOutOfNetwork: boolean
    isClinicalFeeEnabled: boolean
    /** Clinical review fee in dollars; the standard fee when the reference labs don't quote one. */
    clinicalFee: number
    isLoading: boolean
}

const lowestPrice = (prices: Array<number | string | null | undefined>): number | null => {
    const amounts = prices.filter((price) => price != null).map(Number)
    return amounts.length === 0 ? null : Math.min(...amounts)
}

/** Fetches a lab SKU's reference-lab pricing and reduces it to the display amounts. */
export const useLabSkuPricing = (skuId: string | null | undefined): LabSkuPricing => {
    const { apiGet } = useApi()
    const [pricing, setPricing] = useState<Omit<LabSkuPricing, 'isLoading'>>({
        labSku: null,
        cashPrice: null,
        insurancePrice: null,
        isInsuranceOutOfNetwork: false,
        isClinicalFeeEnabled: false,
        clinicalFee: STANDARD_CLINICAL_FEE,
    })
    const [settledSkuId, setSettledSkuId] = useState<string | null>(null)
    const requestedSkuId = useRef(skuId)
    requestedSkuId.current = skuId

    useEffect(() => {
        if (!skuId) return

        const fetchPricing = async () => {
            try {
                const response: LabSkuResponse = await apiGet(`/external/lab_skus/${skuId}`)
                // A response for a SKU the caller has since moved off must not overwrite the current one.
                if (requestedSkuId.current !== skuId) return

                const referenceLabs = [response.prices?.quest, response.prices?.labcorp, response.prices?.bioreference]
                setPricing({
                    labSku: response.sku,
                    cashPrice: lowestPrice(referenceLabs.map((lab) => lab?.test_price)),
                    insurancePrice: lowestPrice(referenceLabs.map((lab) => lab?.insurance_price)),
                    isInsuranceOutOfNetwork: isLabSkuInsuranceOutOfNetwork(response.prices),
                    isClinicalFeeEnabled: referenceLabs.some((lab) => lab?.clinical_fee_enabled),
                    clinicalFee: referenceLabs.find((lab) => lab?.clinical_fee != null)?.clinical_fee ?? STANDARD_CLINICAL_FEE,
                })
            } catch (error) {
                console.error('Failed to fetch lab pricing:', error)
            }
            if (requestedSkuId.current === skuId) setSettledSkuId(skuId)
        }
        fetchPricing()
    }, [skuId, apiGet])

    return { ...pricing, isLoading: Boolean(skuId) && settledSkuId !== skuId }
}
